import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { RevealLines, Reveal } from "@/components/ui-blocks";

const HERO_IMAGE = "/brand/placeholder.jpg";

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.75]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* --- Background Image w/ Parallax --- */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Modern commercial construction site"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-obsidian-950 z-[1]" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-obsidian-950/20 via-obsidian-950/10 to-obsidian-950" />

      {/* --- Grid guides --- */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-full">
          <div className="h-full grid grid-cols-12 gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className={`h-full ${i === 0 || i === 11 ? "border-l border-white/[0.04]" : "border-l border-white/[0.02]"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* --- Content --- */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-40 lg:pt-48 pb-28 lg:pb-36 min-h-[100svh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center justify-between gap-8"
        >
          <div className="flex items-center gap-4">
            <span className="w-2 h-2 bg-accent block" />
            <span className="overline text-ink-200">Southeast Michigan · Since 1999</span>
          </div>
          <div className="hidden md:flex items-center gap-4 text-ink-400 text-xs uppercase tracking-[0.2em]">
            <span>Commercial</span>
            <span className="w-1 h-1 bg-ink-600 rounded-full" />
            <span>Industrial</span>
            <span className="w-1 h-1 bg-ink-600 rounded-full" />
            <span>Multifamily</span>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:items-end">
          <div className="lg:col-span-9">
            <RevealLines
              tag="h1"
              delay={0.2}
              className="font-display text-[16vw] md:text-[13vw] lg:text-[10.5vw] leading-[0.82] uppercase tracking-[-0.02em] text-ink-50"
              lines={[
                "Building",
                "Projects",
                <span key="2"><span className="text-stroke">That</span> <span className="text-accent">Last.</span></span>,
              ]}
            />
          </div>

          <Reveal delay={1.05} y={30} className="lg:col-span-3 flex flex-col justify-end">
            <p className="text-ink-200 text-sm lg:text-base leading-relaxed max-w-md">
              Commercial, industrial, multifamily, and residential construction — delivered with uncompromising quality and 25+ years of Southeast Michigan know-how.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="hero-primary-cta" className="btn-accent">
                Request a Quote <ArrowUpRight size={16} className="btn-arrow" />
              </Link>
              <Link to="/our-work" data-testid="hero-secondary-cta" className="btn-outline">
                View Our Work <ArrowUpRight size={16} className="btn-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      {/* --- Scroll indicator --- */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
      >
        <span className="overline text-[9px] text-ink-400">Scroll</span>
        <span className="block w-px h-10 bg-gradient-to-b from-accent to-transparent" />
      </motion.div>
    </section>
  );
}
